import { createContext, useCallback, useContext, useRef } from 'react'

export interface ToolbarContextValue {
	isAnimating: boolean
	showContent: boolean
	registerItem: () => number
}

const ToolbarContext = createContext<ToolbarContextValue | null>(null)

export const ToolbarProvider = ToolbarContext.Provider

/**
 * Hook to access toolbar context
 * Must be used within a CollapsibleToolbar component
 */
export function useToolbarContext(): ToolbarContextValue {
	const context = useContext(ToolbarContext)
	if (!context) {
		throw new Error('useToolbarContext must be used within CollapsibleToolbar')
	}
	return context
}

/**
 * Hook to create and manage the toolbar context value
 * Handles auto-registration of items via index counter
 * Resets index on each render to ensure consistent ordering
 */
export function useToolbarContextValue(
	isAnimating: boolean,
	showContent: boolean
): ToolbarContextValue {
	const indexRef = useRef(0)

	// Reset index at the start of each render
	indexRef.current = 0

	const registerItem = useCallback(() => {
		const current = indexRef.current
		indexRef.current++
		return current
	}, [])

	return {
		isAnimating,
		showContent,
		registerItem,
	}
}
